import { z } from "zod"

import { avToolEingabeBasisSchema } from "./av-tool"
import { loesungVorschlagSchema, LOESUNG_PRODUKTE } from "./loesung-produkte"

/**
 * Laufzeit-Schemas. Werden im Backend zur Validierung eingehender Bodies
 * genutzt und im Frontend zum Prüfen von Formularen.
 *
 * Im Prototyp wurden API-Bodies ungeprüft gecastet. Mit echten Accounts und
 * Mandantentrennung ist das nicht mehr vertretbar.
 */

export const difficultySchema = z.enum(["entry", "medium", "advanced"])

/**
 * The operating modes of the coach chat.
 *
 * `open`: the coach answers questions. `training`: it asks them and assesses
 * the replies. `onboarding`: the guided first conversation that builds the
 * learning essence. `preparation`: sparring before a real appointment.
 * `nachbereitung`: the debrief of a conversation the advisor has already held.
 * `pruefung`: the exam preparation, where the questions come from the catalog
 * rather than from the model.
 * Carried per message so a thread with mixed sections stays readable afterwards.
 */
export const chatModeSchema = z.enum([
  "open",
  "training",
  "onboarding",
  "preparation",
  "nachbereitung",
  "pruefung",
  "idd",
])

/**
 * Die fünf Phasen des qualifizierten Vertriebsprozesses.
 *
 * Schnitt nach dem Handout „Verkaufsphasen", Masterunterlage S. 12 und der
 * Verkaufstraining-Agenda S. 33: Bedarfsanalyse und Lösungspräsentation sind
 * getrennte Rollenspiele, die Lösung gehört zum Abschluss. Die Handouts heißen
 * abweichend „4. Bedarfsanalyse + Lösung" und „5. Einwandbehandlung", weil sie
 * Formulierungen danach bündeln, wo sie gesprochen werden.
 */
export const gespraechsPhaseSchema = z.enum([
  "terminierung",
  "gespraechsvorbereitung",
  "gespraechsrahmen",
  "bedarfsanalyse",
  "loesung_abschluss",
])
export type GespraechsPhase = z.infer<typeof gespraechsPhaseSchema>

/**
 * Ein abwählbarer Bestandteil innerhalb eines Leitfadens.
 *
 * Nicht ein Lehrgang: ein eigener Lehrgang bekommt laut ARCHITEKTUR.md eine
 * eigene Scoping-Entität. Der Einkommensschutz ist keiner — der IVFP-Prozess
 * bleibt mit ihm derselbe und wird nur an zwei Punkten ergänzt (Anker früh im
 * Gespräch, Klärung in der Lösungsphase).
 */
export const playbookBausteinSchema = z.enum(["einkommensschutz"])
export type PlaybookBaustein = z.infer<typeof playbookBausteinSchema>

/** The whole list, as the platform page shows it. */
export const updateTenantBausteineSchema = z.object({
  bausteine: playbookBausteinSchema.array(),
})
export type UpdateTenantBausteineInput = z.infer<typeof updateTenantBausteineSchema>

/**
 * The five training units of the IVFP training.
 *
 * The unit is the training axis: selection, roleplay, reflection and feedback
 * all sit on it. The phase stays below it as the content key for playbook,
 * rubrics and debrief, because the two are not congruent: `rahmen_bedarf`
 * bundles two phases into one continuous conversation with one shared feedback
 * (customer implementation list of 2026-09-11).
 */
export const trainingsEinheitSchema = z.enum([
  "terminierung",
  "gespraechsvorbereitung",
  "rahmen_bedarf",
  "loesung_abschluss",
  "nachbereitung",
])
export type TrainingsEinheit = z.infer<typeof trainingsEinheitSchema>

/**
 * Existing customer or cold call. Only distinguishes the opening sentences of
 * the appointment call, hence an option on the run and not a second persona.
 */
export const akquiseArtSchema = z.enum(["bestand", "kaltakquise"])
export type AkquiseArt = z.infer<typeof akquiseArtSchema>

/**
 * Der Zahlensatz eines Trainingsfalls.
 *
 * Im echten Beratungsgespräch stehen diese Werte im AV-Tool auf dem Bildschirm.
 * Die KI rechnet nicht, sie reagiert auf feste Zahlen. Der Satz befüllt beim
 * Start eines Durchlaufs den `caseState` vor und macht damit Bedarfsanalyse und
 * Lösungspräsentation ohne AV-Tool trainierbar.
 *
 * Netto, Brutto und GRV-Beitrag stammen aus der Flyer-Tabelle
 * „Einkommensgutachten" (Stand 01.2026, Steuerklasse I).
 */
export const personaFallSchema = z.object({
  /** Woraus der Kontakt entsteht (Eh-da-Anlass, Handout 1.1 S. 4). */
  anlass: z.string(),
  /**
   * Welche Aufhänger zu diesem Fall passen. Bewertungsgrundlage fürs Feedback,
   * wird dem Berater im Briefing bewusst nicht gezeigt: den Anlass zu erkennen
   * ist die trainierte Leistung.
   */
  passendeHooks: z.array(z.string()),
  nettoEinkommen: z.number(),
  bruttoEinkommen: z.number(),
  /** Arbeitnehmer- und Arbeitgeberanteil zusammen, das „700-EUR-Argument". */
  grvBeitragMonatlich: z.number(),
  grvAnwartschaft: z.number(),
  privateAnwartschaft: z.number(),
  jahreBisRente: z.number().int(),
  wunschrente: z.number(),
  luecke: z.number(),
  lueckeMitInflation: z.number(),
  rateKlassisch: z.number(),
  rateInvestiert: z.number(),
  /**
   * Die Gesundheitslage, aus der die drei Optionen des Gesundheitschecks
   * entstehen (Bedarfsanalyse+Lösung S.10/11): ohne Vorerkrankung, mit
   * Vorbelastung, schwer erkrankt.
   *
   * Optional, und das ist keine Stilfrage: `toPersonaDto` parst mit
   * `personaFallSchema.catch(EMPTY_FALL)`. Ein Pflichtfeld ließe bei JEDER
   * bestehenden Persona den gesamten Zahlensatz auf Nullen zurückfallen.
   */
  gesundheit: z.enum(["unbelastet", "vorbelastet", "schwer"]).optional(),
  /** Was der Kunde von sich aus dazu sagen würde, in seinen Worten. */
  gesundheitHinweis: z.string().optional(),
})
export type PersonaFall = z.infer<typeof personaFallSchema>

/**
 * Auswahllisten des Kundenprofils, Wert und Anzeigetext beieinander.
 *
 * Die deutschen Texte stehen hier und nicht im Frontend, weil sie an zwei
 * Stellen gebraucht werden: in der Oberfläche als Beschriftung und im
 * Rollenspiel-Prompt als ausgeschriebener Satzbaustein. Zwei Listen würden
 * auseinanderlaufen, und der Prompt bekäme irgendwann „bu" statt
 * „Berufsunfähigkeitsversicherung".
 */
export const ANLAGEFORMEN = [
  { value: "sparbuch", label: "Sparbuch" },
  { value: "tagesgeld", label: "Tagesgeld" },
  { value: "festgeld", label: "Festgeld" },
  { value: "fonds", label: "Fondsdepot" },
  { value: "aktien", label: "Aktien" },
  { value: "etf", label: "ETF-Sparplan" },
  { value: "bausparvertrag", label: "Bausparvertrag" },
  { value: "gold", label: "Gold" },
] as const
export type Anlageform = (typeof ANLAGEFORMEN)[number]["value"]

export const ALTERSVORSORGE_ARTEN = [
  { value: "riester", label: "Riester-Rente" },
  { value: "ruerup", label: "Rürup-Rente" },
  { value: "bav", label: "betriebliche Altersvorsorge" },
  { value: "private_rv", label: "private Rentenversicherung" },
  { value: "fondssparplan", label: "Fondssparplan zur Vorsorge" },
  { value: "immobilie", label: "Immobilie als Vorsorge" },
] as const
export type AltersvorsorgeArt = (typeof ALTERSVORSORGE_ARTEN)[number]["value"]

export const VERTRAGSARTEN = [
  { value: "bu", label: "Berufsunfähigkeitsversicherung" },
  { value: "haftpflicht", label: "Privathaftpflicht" },
  { value: "hausrat", label: "Hausratversicherung" },
  { value: "risiko_lv", label: "Risiko-Lebensversicherung" },
  { value: "bauspar", label: "Bausparvertrag" },
] as const
export type Vertragsart = (typeof VERTRAGSARTEN)[number]["value"]

export const GESCHLECHT_OPTIONEN = [
  { value: "weiblich", label: "weiblich" },
  { value: "maennlich", label: "männlich" },
  { value: "divers", label: "divers" },
] as const

export const FAMILIENSTAND_OPTIONEN = [
  { value: "ledig", label: "ledig" },
  { value: "verheiratet", label: "verheiratet" },
  { value: "geschieden", label: "geschieden" },
  { value: "verwitwet", label: "verwitwet" },
] as const

export const BESCHAEFTIGUNG_OPTIONEN = [
  { value: "angestellt", label: "angestellt" },
  { value: "selbststaendig", label: "selbstständig" },
  { value: "beamtet", label: "verbeamtet" },
  { value: "ausbildung", label: "in Ausbildung" },
  { value: "nicht_berufstaetig", label: "nicht berufstätig" },
] as const

export const RENTENSTATUS_OPTIONEN = [
  { value: "regulaer", label: "regulär versichert" },
  { value: "luecken", label: "Lücken in der Erwerbsbiografie" },
  { value: "freiwillig", label: "freiwillig versichert" },
  { value: "nicht_versichert", label: "nicht gesetzlich versichert" },
] as const

export const VERSORGUNGSLUECKE_OPTIONEN = [
  { value: "unbekannt", label: "unbekannt" },
  { value: "grob_bekannt", label: "grob bekannt" },
  { value: "genau_bekannt", label: "genau bekannt" },
] as const

const enumFrom = <T extends string>(
  entries: readonly { readonly value: T }[]
) => z.enum(entries.map((entry) => entry.value) as [T, ...T[]])

export const anlageformSchema = enumFrom(ANLAGEFORMEN)
export const altersvorsorgeArtSchema = enumFrom(ALTERSVORSORGE_ARTEN)
export const vertragsartSchema = enumFrom(VERTRAGSARTEN)
export const geschlechtSchema = enumFrom(GESCHLECHT_OPTIONEN)
export const familienstandSchema = enumFrom(FAMILIENSTAND_OPTIONEN)
export const beschaeftigungSchema = enumFrom(BESCHAEFTIGUNG_OPTIONEN)
export const rentenStatusSchema = enumFrom(RENTENSTATUS_OPTIONEN)
export const versorgungslueckeSchema = enumFrom(VERSORGUNGSLUECKE_OPTIONEN)

/**
 * Die Persönlichkeit als fünf bipolare Achsen von 1 bis 5.
 *
 * Bipolar und nicht als Ja/Nein-Schalter, weil „skeptisch: nein" nichts
 * aussagt — die Gegenrichtung ist eine eigene Haltung, die das Rollenspiel
 * anders führt. Die Pole tragen ihre Formulierung mit, damit
 * `renderTraitRules` daraus eine Verhaltensregel bauen kann statt eines
 * Datenblatts: eine Persona, die ihre eigenen Kennwerte vorliest, ist keine.
 *
 * Die Mitte (3) ist nicht neutral gemeint, sondern unauffällig: aus ihr
 * entsteht bewusst KEINE Prompt-Zeile.
 */
export const PERSONA_TRAITS = [
  {
    key: "beratungsoffenheit",
    label: "Offen für Beratung",
    low: "verschlossen",
    high: "offen",
    regelNiedrig:
      "Beratung ist dir erst mal unangenehm. Du hältst dich zurück und gibst wenig von dir preis.",
    regelHoch:
      "Du bist für Beratung offen, hörst zu und erzählst von dir, wenn echtes Interesse an dir spürbar ist.",
  },
  {
    key: "versicherungsskepsis",
    label: "Skeptisch gegenüber Versicherungen",
    low: "vertrauensvoll",
    high: "skeptisch",
    regelNiedrig:
      "Versicherungen hältst du grundsätzlich für sinnvoll und nimmst Empfehlungen wohlwollend auf.",
    regelHoch:
      "Versicherungen begegnest du misstrauisch. Du vermutest, dass am Ende vor allem die Bank verdient, und sagst das auch.",
  },
  {
    key: "preisorientierung",
    label: "Preisorientiert",
    low: "qualitätsorientiert",
    high: "preisorientiert",
    regelNiedrig:
      "Auf den Preis schaust du wenig. Dir ist wichtiger, dass die Lösung wirklich trägt.",
    regelHoch:
      "Du fragst früh nach dem Preis und vergleichst. Ein hoher Beitrag ist für dich das erste Gegenargument.",
  },
  {
    key: "sicherheitsorientierung",
    label: "Sicherheitsorientiert",
    low: "risikobereit",
    high: "sicherheitsorientiert",
    regelNiedrig:
      "Schwankungen machen dir nichts aus. Du würdest für mehr Rendite auch ins Risiko gehen.",
    regelHoch:
      "Sicherheit geht dir über Rendite. Bei allem, was schwanken kann, wirst du vorsichtig und fragst nach Garantien.",
  },
  {
    key: "entscheidungsfreude",
    label: "Entscheidungsfreudig oder zögerlich",
    low: "zögerlich",
    high: "entscheidungsfreudig",
    regelNiedrig:
      "Entscheidungen schiebst du auf. Du willst nichts überstürzen und möchtest lieber noch darüber schlafen.",
    regelHoch:
      "Wenn dich etwas überzeugt, entscheidest du zügig und brauchst keine zweite Nacht darüber.",
  },
] as const
export type PersonaTraitKey = (typeof PERSONA_TRAITS)[number]["key"]

export const personaTraitsSchema = z.object(
  Object.fromEntries(
    PERSONA_TRAITS.map((trait) => [
      trait.key,
      z.number().int().min(1).max(5).optional(),
    ])
  ) as Record<PersonaTraitKey, z.ZodOptional<z.ZodNumber>>
)
export type PersonaTraits = z.infer<typeof personaTraitsSchema>

/**
 * Was der Berater im echten Gespräch im AV-Tool vor sich hätte, jenseits der
 * Zahlen: Familie, Wohnen, Vermögen, Bestandsverträge und die Persönlichkeit.
 *
 * Eigene Spalte und nicht in `fall`, obwohl beides JSON ist: `fall` friert beim
 * Start eines Durchlaufs in `caseState` ein, damit ein späteres Bearbeiten der
 * Vorlage die Zahlen des laufenden Durchlaufs nicht verschiebt. Das Profil ist
 * Identität der Persona und braucht diesen Schnappschuss nicht — es ist über
 * die Versionierung (Bearbeiten = archivieren + neu anlegen) ohnehin stabil.
 *
 * **Jeder Key ist optional, und das ist keine Stilfrage.** `toPersonaDto` parst
 * mit `personaProfilSchema.catch(EMPTY_PROFIL)`. Ein Pflichtfeld ließe bei JEDER
 * Persona ohne dieses Feld das gesamte Profil auf leer zurückfallen — dieselbe
 * Falle wie bei `personaFallSchema.gesundheit`.
 *
 * Bewusst NICHT hier: „Ruhestand in Sicht". Der Abstand zur Rente steht schon in
 * `fall.jahreBisRente`; ein zweites Feld könnte ihm widersprechen. Abgeleitet
 * wird er über `ruhestandInSicht()`.
 */
export const personaProfilSchema = z.object({
  geschlecht: geschlechtSchema.optional(),
  familienstand: familienstandSchema.optional(),
  kinder: z.number().int().min(0).max(8).optional(),
  beschaeftigung: beschaeftigungSchema.optional(),
  /** Neben Netto und Brutto aus `fall`: Miete, Nebenjob, Unterhalt. */
  zusatzeinkommen: z.number().min(0).optional(),
  zusatzeinkommenArt: z.string().max(120).optional(),
  immobilienbesitz: z.boolean().optional(),
  finanzierungVorhanden: z.boolean().optional(),
  /** Freies Geldvermögen, ohne Immobilie und ohne Altersvorsorge. */
  geldvermoegen: z.number().min(0).optional(),
  anlageformen: z.array(anlageformSchema).optional(),
  /** Die Art der Vorsorge; der Betrag steht in `fall.privateAnwartschaft`. */
  altersvorsorge: z.array(altersvorsorgeArtSchema).optional(),
  /**
   * Die Lage in der gesetzlichen Rente; die Beträge stehen in
   * `fall.grvAnwartschaft` und `fall.grvBeitragMonatlich`.
   */
  rentenStatus: rentenStatusSchema.optional(),
  bestehendeVertraege: z.array(vertragsartSchema).optional(),
  /**
   * Ob der Kunde seine Versorgungslücke kennt. Steuert im Rollenspiel, ob er
   * eine Zahl nennen darf: `fall.luecke` wird ihm bewusst nicht gezeigt, weil
   * die Beraterin sie erarbeiten soll.
   */
  versorgungsluecke: versorgungslueckeSchema.optional(),
  persoenlichkeit: personaTraitsSchema.optional(),
})
export type PersonaProfil = z.infer<typeof personaProfilSchema>

/**
 * Wie nah der Ruhestand ist. Abgeleitet statt gepflegt, damit die Angabe dem
 * Rentenbeginn nicht widersprechen kann.
 */
export function ruhestandInSicht(
  jahreBisRente: number
): "nah" | "mittel" | "weit" {
  if (jahreBisRente <= 10) return "nah"
  return jahreBisRente <= 25 ? "mittel" : "weit"
}

/** Values that must be established during the needs-analysis conversation. */
export const NEEDS_ANALYSIS_FIELDS = [
  { key: "nettoEinkommen", label: "Nettoeinkommen" },
  { key: "grvAnwartschaft", label: "gesetzliche Rentenanwartschaft" },
  { key: "privateAnwartschaft", label: "private Rentenanwartschaft" },
  { key: "jahreBisRente", label: "Jahre bis zur Rente" },
  { key: "wunschrente", label: "Wunschrente" },
  { key: "luecke", label: "Vorsorgelücke" },
  { key: "lueckeMitInflation", label: "Vorsorgelücke mit Inflation" },
] as const

export type NeedsAnalysisFieldKey =
  (typeof NEEDS_ANALYSIS_FIELDS)[number]["key"]

export const needsAnalysisFieldKeySchema = z.enum(
  NEEDS_ANALYSIS_FIELDS.map((field) => field.key) as [
    NeedsAnalysisFieldKey,
    ...NeedsAnalysisFieldKey[],
  ]
)

export const needsAnalysisToolFactSchema = z.object({
  field: needsAnalysisFieldKeySchema,
  value: z.number().finite(),
  evidence: z.string().trim().min(1).max(500),
})
export type NeedsAnalysisToolFact = z.infer<typeof needsAnalysisToolFactSchema>

/**
 * What the appointment sidecar may write from the phone call.
 *
 * Day and time are separate fields, and that is the point: with one free-text
 * slot the model answered "no appointment was made" by writing that sentence
 * into it, and the sheet showed a captured appointment reading "Kein Termin
 * vereinbart". Two required fields leave no room for prose — a negation has
 * nowhere to go, and the caller's `vereinbart` flag carries the "no".
 *
 * Both are capped because they come straight out of an untrusted transcript.
 */
export const terminToolFactSchema = z.object({
  /** The day as agreed: "Dienstag, 12.05." */
  tag: z.string().trim().min(1).max(60),
  /** The time as agreed: "16:00 Uhr" */
  uhrzeit: z.string().trim().min(1).max(30),
  evidence: z.string().trim().min(1).max(500),
})
export type TerminToolFact = z.infer<typeof terminToolFactSchema>

/**
 * Ein Einwand in zwei Fassungen.
 *
 * `satz` ist der Einwand, wie ihn das Handout führt. Er ist die Referenz fürs
 * Feedback („das war der Makler-Einwand, Gold-Standard in Handout 1.1 S. 5")
 * und für die Pflege, geht aber NICHT ins Rollenspiel.
 *
 * `haltung` ist die Sorge dahinter, in Alltagssprache. Nur sie bekommt die
 * Persona zu sehen. Wer einem Modell einen fertigen Satz vorlegt, bekommt eine
 * Rezitation; wer ihm die Haltung gibt, bekommt eine Kundenäußerung.
 */
export const personaObjectionSchema = z.object({
  /** Wortlaut aus dem Handout. Referenz fürs Feedback, nicht fürs Rollenspiel. */
  satz: z.string(),
  /** Die Sorge dahinter, in eigenen Worten der Persona. */
  haltung: z.string(),
})
export type PersonaObjection = z.infer<typeof personaObjectionSchema>

/**
 * Einwände je Phase statt eines Einwands für alles.
 *
 * Die Handouts trennen sauber: Termin-Einwände stehen in Handout 1.1,
 * Abschluss-Einwände in Handout 5. Eine Persona, die überall denselben Einwand
 * bringt, trainiert die Einwandbehandlung genau einmal.
 */
export const personaObjectionsSchema = z.object({
  terminierung: personaObjectionSchema.optional(),
  bedarfsanalyse: personaObjectionSchema.optional(),
  loesung_abschluss: personaObjectionSchema.optional(),
  /**
   * Der Einwand zum Einkommensschutz — bewusst KEIN Phasen-Key.
   *
   * `renderObjectionRule` nimmt den ersten Einwand über die Phasen der Einheit,
   * weil der Kunde eine Haltung durchs Gespräch trägt. Ein vierter Phasen-Key
   * würde mit dem Abschluss-Einwand konkurrieren und ihn verdrängen. Dieser
   * Einwand ist additiv und kommt nur, wenn die Beraterin das Thema aufmacht.
   */
  einkommensschutz: personaObjectionSchema.optional(),
})
export type PersonaObjections = z.infer<typeof personaObjectionsSchema>

/**
 * Quellenangabe fürs belegte Feedback (Handout/Seite).
 *
 * `page` ist bewusst optional: Das Modell faltet die Seite gern in den
 * Handout-Namen ("Terminierung S.1") oder lässt sie weg. Eine Pflicht-Zahl
 * ließ dann die GESAMTE Feedback-Validierung scheitern (NoObjectGeneratedError)
 * — eine fehlende Seitenzahl in einer Nebenangabe darf die Auswertung nie
 * killen.
 */
export const sourceRefSchema = z.object({
  handout: z.string().optional(),
  page: z.number().int().optional(),
})

/**
 * Eine Quellenangabe ohne Handout ist keine Quelle.
 *
 * `handout` ist im Schema trotzdem optional, weil das Modell für „keine Quelle"
 * gern ein halbes Objekt liefert statt gar keins. War das Feld Pflicht, ließ ein
 * `sourceRef: { page: 3 }` die GESAMTE Auswertung scheitern. Verworfen wird die
 * unbrauchbare Angabe deshalb hier, nach der Validierung, statt sie das ganze
 * Feedback kosten zu lassen.
 */
export function verwerteQuelle(
  ref: { handout?: string; page?: number } | undefined
): { handout: string; page?: number } | undefined {
  const handout = ref?.handout?.trim()
  return handout ? { handout, page: ref?.page } : undefined
}

/** Entfernt unbrauchbare Quellenangaben aus einem frisch erzeugten Feedback. */
export function normalisiereFeedback<T extends FeedbackOutput>(feedback: T): T {
  return {
    ...feedback,
    dimensions: Object.fromEntries(
      Object.entries(feedback.dimensions).map(([key, dim]) => [
        key,
        { ...dim, sourceRef: verwerteQuelle(dim.sourceRef) },
      ])
    ) as T["dimensions"],
    impulses: feedback.impulses.map((impulse) => ({
      ...impulse,
      sourceRef: verwerteQuelle(impulse.sourceRef),
    })),
    betterPhrases: feedback.betterPhrases.map((p) => ({
      ...p,
      sourceRef: verwerteQuelle(p.sourceRef),
    })),
  }
}

/**
 * All ten realtime voices. This is a response schema, and Prisma stores the
 * voice as a plain string: a voice the seed writes but this enum omits is
 * dropped on serialisation, and the coach silently falls back to the default
 * voice. `echo` and `shimmer` were missing here for a while, which is why
 * Lukas did not sound like the voice he was seeded with — whoever adds a voice
 * to `starter-content.ts` adds it here too.
 */
export const realtimeVoiceSchema = z.enum([
  "alloy",
  "ash",
  "ballad",
  "coral",
  "echo",
  "sage",
  "shimmer",
  "verse",
  "marin",
  "cedar",
])

/**
 * Die fünf Feedback-Dimensionen (Anforderungsdok. §6). Einzige Quelle für Keys
 * und Labels — genutzt im Prompt, im UI und in der Debrief-Aggregation.
 *
 * Die Reihenfolge ist die Priorität, mit der ein realer Coach sie angeht
 * (Check-in 11.08.2026): Fachlichkeit zuerst, persönlicher Stil zuletzt, weil
 * er ohne sitzende Fachlichkeit und Struktur nichts trägt.
 */
export const FEEDBACK_DIMENSIONS = [
  { key: "fachlichkeit", label: "Fachlichkeit" },
  { key: "gespraechsstruktur", label: "Gesprächsstruktur" },
  { key: "kommunikation", label: "Kommunikation" },
  { key: "kundenwirkung", label: "Kundenwirkung" },
  { key: "stil", label: "Persönlicher Stil" },
] as const

export type FeedbackDimensionKey = (typeof FEEDBACK_DIMENSIONS)[number]["key"]

/**
 * Die drei Bewertungsperspektiven, in denen der Kunde denkt, abgebildet auf die
 * fünf internen Dimensionen.
 *
 * Derived on purpose — no key of its own and never persisted. The five
 * dimensions are load-bearing in the prompt, the UI, the debrief aggregation
 * and every stored feedback row; a sixth key would invalidate all of them.
 * Note there is no "Abschluss" perspective: sales effectiveness is a customer
 * effect, so a summary can never be reduced to whether a deal closed.
 */
export const KUNDEN_PERSPEKTIVEN = [
  {
    key: "fachlich",
    label: "Fachlich sauber",
    dims: ["fachlichkeit", "gespraechsstruktur"],
  },
  {
    key: "kundenzentriert",
    label: "Kundenzentriert",
    dims: ["kundenwirkung", "kommunikation", "stil"],
  },
  {
    key: "vertrieblich",
    label: "Vertrieblich wirksam",
    dims: ["kundenwirkung", "gespraechsstruktur"],
  },
] as const satisfies ReadonlyArray<{
  key: string
  label: string
  dims: ReadonlyArray<FeedbackDimensionKey>
}>

export const feedbackDimensionKeySchema = z.enum([
  "fachlichkeit",
  "gespraechsstruktur",
  "kommunikation",
  "kundenwirkung",
  "stil",
])

/** Bewertung einer einzelnen Feedback-Dimension. */
export const feedbackDimensionSchema = z.object({
  score: z.number().int().min(1).max(5),
  evidence: z.string(),
  // Legacy: newer feedback carries no per-dimension recommendation — all
  // actionable advice is consolidated into the ranked `impulses` instead.
  recommendation: z.string().optional(),
  // Belegt-statt-geraten: Zitat aus dem Transkript + Leitfaden-Quelle.
  transcriptQuote: z.string().optional(),
  sourceRef: sourceRefSchema.optional(),
})

/** Eine belegte Stärke, verortet in einer der fünf Dimensionen. */
export const feedbackStrengthSchema = z.object({
  dimension: feedbackDimensionKeySchema,
  title: z.string().describe("Knapp, wird als Überschrift gezeigt."),
  text: z.string().describe("Was gut war und warum es gewirkt hat."),
  transcriptQuote: z.string().optional(),
  /**
   * Woher das Zitat stammt. Absent means the roleplay transcript.
   *
   * The trap: `transcriptQuote` is named after the roleplay, but a summary
   * built from a reflection dialogue legitimately quotes what the advisor said
   * about herself ("du hast selbst gesagt …"). Without this marker the eval's
   * verbatim check would search the wrong corpus and discard a valid quote.
   */
  quoteSource: z.enum(["gespraech", "selbstreflexion"]).optional(),
})
export type FeedbackStrength = z.infer<typeof feedbackStrengthSchema>

/**
 * Ein Entwicklungsimpuls. Die Array-Reihenfolge ist die Priorität: der erste
 * Impuls ist der größte Hebel für die Wirksamkeit des nächsten Gesprächs.
 */
export const feedbackImpulseSchema = z.object({
  dimension: feedbackDimensionKeySchema,
  title: z.string().describe("Knapp, als Handlungsaufforderung."),
  leverage: z
    .string()
    .describe(
      "Warum genau hier der größte Hebel liegt, mit Bezug aufs Gespräch. " +
        "Höchstens zwei Sätze."
    ),
  nextStep: z
    .string()
    .describe(
      "Was sie im nächsten Rollenspiel konkret tut. EIN Satz, ohne Zitat — " +
        "der wörtliche Satz gehört in beispielsatz."
    ),
  /**
   * Der Satz, den sie wörtlich sagen kann. Eigenes Feld, damit er als Zitat
   * gesetzt wird statt hinten am Absatz zu kleben; Alt-Feedback hat ihn noch im
   * nextStep, deshalb optional.
   */
  beispielsatz: z
    .string()
    .optional()
    .describe(
      "Ein Satz in Anführungszeichen-Qualität, den sie wörtlich sagen kann."
    ),
  reflexionsfrage: z
    .string()
    .optional()
    .describe(
      "Eine Frage, mit der sie selbst auf die Stelle schaut. Kein Test, keine " +
        "Suggestivfrage."
    ),
  optionen: z
    .array(z.string())
    .optional()
    .describe(
      "Zwei bis drei Wege, die sie ausprobieren kann, als Angebot formuliert. " +
        "Was sie im Gespräch selbst gewählt oder erarbeitet hat, steht vorn."
    ),
  sourceRef: sourceRefSchema.optional(),
  /** Slug der hinterlegten Wirkungslogik, auf die sich der Impuls stützt. */
  wirkungsLogikSlug: z.string().optional(),
  /**
   * Wie der Impuls zur Selbsteinschätzung der Beraterin steht. Nur gesetzt,
   * wenn das Feedback aus einem Reflexionsgespräch entstanden ist.
   */
  selbstbildBezug: z
    .enum(["bestaetigt", "erweitert", "blinder_fleck"])
    .optional()
    .describe(
      "bestaetigt: sie hat es selbst erkannt. erweitert: sie hat es angerissen. " +
        "blinder_fleck: sie hat es nicht gesehen."
    ),
})
export type FeedbackImpulse = z.infer<typeof feedbackImpulseSchema>

/**
 * Der Transfer-Block: was die Beraterin beim nächsten Mal konkret anders macht.
 *
 * Deliberately optional in {@link feedbackSchema} even though the customer
 * requires it. Two reasons: `toValidFeedbackDto` discards an ENTIRE feedback
 * row that fails to parse, so a mandatory field would silently drop every
 * pre-existing feedback from the history; and the transcript-only fallback path
 * has no dialogue to derive a transfer from. It is required by the prompt and
 * checked by the eval instead — same principle as the missing `.max()` on the
 * list fields below.
 */
export const feedbackTransferSchema = z.object({
  naechsteAnwendung: z
    .string()
    .describe("In welcher konkreten nächsten Situation sie es anwendet."),
  konkreterSatz: z
    .string()
    .optional()
    .describe("Ein Satz, den sie wörtlich sagen kann."),
  woranErkennbar: z
    .string()
    .describe("Woran sie im Gespräch merkt, dass es gelingt."),
})
export type FeedbackTransfer = z.infer<typeof feedbackTransferSchema>

/**
 * Abgleich von Selbstbild und Beobachtung.
 *
 * Der eigentliche Nutzen des Reflexionsgesprächs: nicht die Bewertung, sondern
 * die Differenz zwischen dem, was die Beraterin über ihr Gespräch denkt, und
 * dem, was im Gespräch zu sehen war.
 */
export const feedbackAbgleichSchema = z.object({
  uebereinstimmung: z
    .array(z.string())
    .default([])
    .describe("Wo ihre Sicht und die Beobachtung übereinstimmen. Bestärkend."),
  blindeFlecken: z
    .array(z.string())
    .default([])
    .describe(
      "Wo die Beobachtung von ihrer Sicht abweicht, als Hypothese und " +
        "Reflexionsangebot formuliert, nicht als Fehler."
    ),
  unterschaetzt: z
    .array(z.string())
    .default([])
    .describe("Was sie schlechter bewertet hat, als es war."),
})
export type FeedbackAbgleich = z.infer<typeof feedbackAbgleichSchema>

/** A behaviour that recurred in the roleplay, with its effect on the customer. */
export const feedbackMusterSchema = z.object({
  titel: z.string().describe("Knapp, beschreibend, ohne Wertung."),
  beobachtung: z
    .string()
    .describe("Was wiederholt auftrat, mit einem konkreten Gesprächsbeispiel."),
  wirkung: z
    .string()
    .describe("Welche Wirkung das vermutlich auf den Kunden hatte."),
  transcriptQuote: z.string().optional(),
})
export type FeedbackMuster = z.infer<typeof feedbackMusterSchema>

/**
 * The transfer agreement in the advisor's own decisions.
 *
 * Supersedes {@link feedbackTransferSchema} for feedback from a reflection
 * dialogue; old rows keep their `transfer` and still render it.
 */
export const feedbackVereinbarungSchema = z.object({
  mindestziel: z
    .string()
    .optional()
    .describe("Die kleine, realistische Veränderung, die sie sofort umsetzt."),
  ziel: z
    .string()
    .optional()
    .describe("Was sie in den nächsten Tagen oder Wochen verbessern will."),
  hoechstziel: z
    .string()
    .optional()
    .describe("Woran sie erkennt, dass sie die Kompetenz sicher beherrscht."),
  experiment: z
    .string()
    .optional()
    .describe("Was einen Versuch wert ist, ohne sich festzulegen."),
  bewusstNichtUebernommen: z
    .string()
    .optional()
    .describe(
      "Ein Impuls, den sie verstanden hat, aber jetzt nicht umsetzen will. " +
        "Nur wenn sie das gesagt hat."
    ),
  meilenstein: z
    .string()
    .optional()
    .describe("Wann und anhand welcher Beobachtung sie ihren Fortschritt prüft."),
})
export type FeedbackVereinbarung = z.infer<typeof feedbackVereinbarungSchema>

// No upper bounds on the list fields on purpose: OpenAI structured outputs
// cannot enforce minItems/maxItems, so the model regularly returns a complete,
// useful object with one item too many. A hard `.max()` here would reject that
// object on both sides (server `result.output` and the client's `useObject`),
// discarding fully generated feedback and forcing a visible re-stream. Counts
// are capped where they are displayed instead. `strengths` has NO min at all —
// see below.
/**
 * Struktur, die das Modell beim Feedback erzeugen muss.
 *
 * Property order matters: the model emits the JSON in schema order and the
 * feedback is streamed, so the order here IS the order in which the sheet fills
 * in. It therefore follows `feedback-panel.tsx` top-to-bottom:
 * rueckspiegelung → muster → abgleich → strengths → impulses → vereinbarung
 * → transfer (mit betterPhrases darin) → zusammenfassung → mutmacher. Alles,
 * was das Blatt nicht zeigt — dimensions, overall,
 * iddClassification, suggestedQuestions — steht dahinter, damit es den
 * sichtbaren Abschnitten nicht die Zeit wegnimmt.
 *
 * Die eine Ausnahme ist `zusammenfassung`: sie steht im Blatt oben, wird aber
 * zuletzt geschrieben, weil sie zusammenfasst, was vor ihr entsteht. Ihren
 * Platz hält das Blatt während des Streams frei; die Begründung steht am Feld.
 *
 * Wer hier umsortiert, bricht die Reihenfolge des Blatts. Der Test
 * „Feldreihenfolge" in `apps/web/tests/feedback-panel.test.tsx` hält das fest —
 * ein Kommentar allein hat diese Zusage schon einmal nicht gehalten.
 */
export const feedbackSchema = z.object({
  /**
   * Norberts letzter Gesprächsbeitrag: was er aus dem Gespräch mitnimmt, in
   * seinen Worten.
   *
   * Bewusst das ERSTE Feld. Die Feldreihenfolge ist die Streaming-Reihenfolge,
   * also steht dieser Satz sofort im Verlauf, während das Dokument dahinter noch
   * entsteht. So endet das Reflexionsgespräch mit einem Beitrag und nicht mit
   * einem Bericht — die Menschlichkeit, die der Kunde verlangt, entsteht laut
   * seiner Vorgabe durch Interaktion, nicht durch Gestaltungselemente.
   *
   * Optional wie alle neuen Felder: `toValidFeedbackDto` verwirft eine ganze
   * Feedback-Zeile, die nicht gegen das Schema parst.
   */
  abschlussBeitrag: z
    .string()
    .optional()
    .describe(
      "2-4 Sätze an die Beraterin, du-Form, gesprochene Sprache. Knüpft an ihre " +
        "eigenen Worte an, nennt die Stärke und den einen Hebel. Keine " +
        "Aufzählung, keine Überschriften, keine Note."
    ),
  rueckspiegelung: z
    .string()
    .optional()
    .describe(
      "Zwei bis drei Sätze, du-Form: ihre Zahl auf der Skala, was ihr aus " +
        "ihrer Sicht gelungen ist, was ihr gefehlt hat und ihr Auftrag an dich. " +
        "Ihre Worte, nicht deine Bewertung."
    ),
  muster: z.array(feedbackMusterSchema).optional(),
  /** Only for feedback from a reflection dialogue. */
  abgleich: feedbackAbgleichSchema.optional(),
  // Strengths may be empty on purpose: a near-empty conversation (e.g. only a
  // greeting) genuinely has no strength, and a strong model correctly returns
  // []. Forcing `.min(1)` made it invent praise — or, once the model got good
  // enough to refuse, the whole object failed validation (NoObjectGeneratedError)
  // and the advisor saw an error instead of an honest "nothing worked yet".
  /** Belegte Stärken, stärkste zuerst. Darf leer sein, wenn es keine gibt. */
  strengths: z.array(feedbackStrengthSchema),
  /**
   * Entwicklungsimpulse, gerankt nach Hebelwirkung (größter Hebel zuerst).
   *
   * `min(1)`: ein sauberes Gespräch darf mit einem Impuls auskommen. Eine
   * `.max(3)` fehlt aus demselben Grund wie oben — gekappt wird beim Rendern.
   */
  impulses: z.array(feedbackImpulseSchema).min(1),
  vereinbarung: feedbackVereinbarungSchema.optional(),
  /**
   * Hinweise für die nächste Anwendung. Steht im Blatt als Karte „Beim nächsten
   * Mal" unter den Impulsen, deshalb hier und nicht weiter unten.
   */
  transfer: feedbackTransferSchema.optional(),
  /**
   * Bessere Formulierungen. Direkt hinter `transfer`, weil sie im Blatt IN
   * dessen Karte stehen („So könntest du es sagen") und nicht in einer eigenen:
   * käme sie davor, füllte sich die Karte von unten.
   */
  betterPhrases: z.array(
    z.object({
      situation: z.string(),
      phrase: z.string(),
      // Optional: for a self-evident phrase the model may skip the rationale,
      // and a missing whyItWorks must not sink the whole feedback object.
      whyItWorks: z.string().optional(),
      sourceRef: sourceRefSchema.optional(),
    })
  ),
  /**
   * The unit in three sentences, one level above the individual items.
   *
   * Produced in the same model call as the detail, not in a second one: the
   * abstraction is worth nothing if it comes from a model that did not just
   * write what it abstracts, and a separate call would double the cost of every
   * unit. Optional because feedback written before this field will never be
   * regenerated.
   *
   * Das ist zugleich die Begründung für die Position, und sie ist die eine
   * Ausnahme von der Regel oben: im Blatt steht die Bilanz GANZ OBEN, geschrieben
   * wird sie hier, hinter allem, was sie zusammenfasst. Nach vorn geholt müsste
   * das Modell abstrahieren, bevor es die Details formuliert hat — dann wäre der
   * Dreizeiler eine Vorhersage und keine Bilanz.
   *
   * Dass sie deshalb zuletzt einläuft, darf man nicht sehen: `FeedbackPanel`
   * hält ihren Platz während des Streams mit Skelettzeilen frei, damit die Karte
   * nicht am Ende über das ganze Blatt springt. Wer hier umsortiert, muss dort
   * nachsehen.
   */
  zusammenfassung: z
    .object({
      staerke: z
        .string()
        .describe("Was über die Einheit hinweg getragen hat, ein Satz."),
      hebel: z
        .string()
        .describe("Der eine Punkt mit dem größten Hebel, ein Satz."),
      transfer: z
        .string()
        .describe("Was sie ins nächste Gespräch mitnimmt, ein Satz."),
    })
    .optional(),
  /**
   * Das Schlusswort des Coaches unter dem Blatt.
   *
   * Steht nach der Bilanz, weil die Feldreihenfolge die Streaming-Reihenfolge
   * ist: der Schlusspunkt läuft ein, wenn das Dokument sonst fertig ist.
   * Getrennt von {@link feedbackSchema} `abschlussBeitrag`: der ist der
   * gesprochene Übergang im Verlauf und verweist hierauf, ohne ihn zu
   * wiederholen.
   *
   * Optional wie alle neuen Felder — verbindlich macht ihn der Prompt, nicht
   * das Schema, sonst verwürfe `toValidFeedbackDto` jedes Bestandsfeedback.
   */
  mutmacher: z
    .string()
    .optional()
    .describe(
      "Zwei bis drei Sätze zum Schluss, du-Form, im Ton des Coaches. Macht Mut, " +
        "ohne zu beschönigen. Keine Aufzählung, keine Überschrift, keine Note."
    ),
  /**
   * Die fünf festen Dimensionen (jeweils belegt bewertet). Interne
   * Beobachtungsmatrix: sie begründet die Auswahl der Impulse und trägt die
   * Aggregation über mehrere Phasen, wird dem Berater aber nicht als
   * Bewertungstabelle vorgelegt.
   *
   * Deshalb hinter allem, was im Blatt steht: fünf bewertete Dimensionen mit
   * Beleg und Zitat sind viel Text, und davor stand er vor den sichtbaren
   * Abschnitten. Die Impulse laufen weiterhin vor den Dimensionen ein — an dem
   * Verhältnis ändert die Verschiebung nichts.
   */
  dimensions: z.object({
    fachlichkeit: feedbackDimensionSchema,
    gespraechsstruktur: feedbackDimensionSchema,
    kommunikation: feedbackDimensionSchema,
    kundenwirkung: feedbackDimensionSchema,
    stil: feedbackDimensionSchema,
  }),
  /**
   * Ein bis zwei Sätze über den Verlauf des Reflexionsgesprächs. Geht in den
   * Debrief ein, damit dort auftaucht, was die Beraterin selbst erkannt hat.
   */
  gespraechsnotiz: z.string().optional(),
  /**
   * Gesamt-Block — wird im Phasen-Feedback nicht angezeigt, aber persistiert und
   * vom Debrief (summary/strongestMoment/biggestRisk/practiceFocus) genutzt.
   */
  overall: z.object({
    summary: z.string().describe("Kompakte Kernaussage in 1-2 Sätzen."),
    strongestMoment: z
      .string()
      .describe("Konkret, mit Bezug auf das Gespräch."),
    biggestRisk: z.string().describe("Konkret, mit Bezug auf das Gespräch."),
    practiceFocus: z.object({
      title: z.string(),
      reason: z.string(),
      nextExercise: z.string(),
      difficulty: difficultySchema,
    }),
  }),
  /**
   * Nachweisdaten für die IDD-Weiterbildungszeit. `themenfeld` und `lerninhalt`
   * statt Produkt und Trainingsziel: der Lehrgang ist nicht produktgetrieben,
   * die IDD-Zeit wird je Maßnahme und Inhalt ausgewiesen (Prozesschart S. 6/7).
   */
  iddClassification: z.object({
    isIddRelevant: z.boolean(),
    themenfeld: z.string(),
    lerninhalt: z.string(),
    knowledgeVersion: z.string(),
  }),
  /**
   * Klickbare Schnellfragen für den Coach-Chat unter dem Feedback.
   * Deliberately the last content field: the pills only render once the
   * feedback is complete, so they must not delay the streamed sections above.
   */
  suggestedQuestions: z
    .array(z.string())
    .min(3)
    .describe(
      "Kurze Anschlussfragen an den Coach, konkret zu diesem Feedback."
    ),
  /** Auf welche Phase sich dieses Feedback bezieht (Etappen-Feedback). */
  phase: gespraechsPhaseSchema.optional(),
})

export type FeedbackOutput = z.infer<typeof feedbackSchema>

/**
 * Die vier Stufen des Reflexionsgesprächs nach einem Rollenspiel.
 *
 * Das Feedback entsteht IM Gespräch, nicht davor: der Coach kennt Protokoll,
 * Fall und Leitfaden von Anfang an, aber es gibt kein vorformuliertes Urteil,
 * das nur noch enthüllt wird. Was sich zwischen den Stufen ändert, ist sein
 * Auftrag, nicht sein Wissen.
 *
 * The stage therefore gates *behaviour*, not context — which means the
 * restraint in `selbstreflexion` rests on the prompt alone. That is what the
 * `bewertung-zu-frueh` eval check exists for.
 */
export const REFLEXIONS_STUFEN = [
  { key: "einstieg", label: "Einstieg" },
  { key: "selbstreflexion", label: "Deine Sicht" },
  { key: "analyse", label: "Gemeinsame Analyse" },
  { key: "abschluss", label: "Feedback" },
] as const

export type ReflexionsStufe = (typeof REFLEXIONS_STUFEN)[number]["key"]

export const reflexionsStufeSchema = z.enum([
  "einstieg",
  "selbstreflexion",
  "analyse",
  "abschluss",
])

/**
 * Die Selbsteinschätzung der Beraterin, wie sie im Gespräch entsteht.
 *
 * Die vier Reflexionsfragen als eigene Felder statt als ein Freitextblock: nur
 * so ist „die Selbsteinschätzung liegt vor" prüfbar, bevor die Analyse
 * freigeschaltet wird. Drei Beiträge können drei Mal „weiß nicht" sein.
 *
 * Sensible Mitarbeiterdaten. Gehört nie in ein Listen-DTO und nie in eine
 * Aggregation: der Berater entscheidet, was ein Coach oder eine Führungskraft
 * von seinem Training sieht.
 */
export const selbstbildSchema = z.object({
  /** Her answer to the scaling question, 1 to 10. */
  zufriedenheit: z.number().int().min(1).max(10).optional(),
  /** Wie hast du das Gespräch erlebt? */
  erlebt: z.string().optional(),
  /** Was ist dir gut gelungen? */
  gelungen: z.array(z.string()).default([]),
  /** Worauf bist du stolz? */
  stolz: z.string().optional(),
  /** What was missing for a higher score, or what she would do differently. */
  andersMachen: z.array(z.string()).default([]),
  /** What she wants the coach's help with. */
  coachingAuftrag: z.string().optional(),
  offeneFragen: z.array(z.string()).default([]),
  /**
   * Eigene Noten je Dimension. Nur wenn die Beraterin von sich aus bewertet —
   * es wird ausdrücklich nicht danach gefragt, weil die Reflexion sonst zur
   * Selbstbenotung wird.
   */
  eigeneNoten: z
    .record(feedbackDimensionKeySchema, z.number().int().min(1).max(5))
    .optional(),
  vollstaendigkeit: z.enum(["duenn", "tragfaehig", "reich"]),
  erfasstAus: z.enum(["text", "voice"]),
})
export type Selbstbild = z.infer<typeof selbstbildSchema>

/**
 * Ein Stufenwechsel im Reflexionsgespräch.
 *
 * `ausloeser` ist nicht Protokoll, sondern Regel: bei `nutzer` werden die
 * Freigabebedingungen bewusst übergangen. Wer seine Einschätzung jetzt hören
 * will, bekommt sie — die Hoheit über das Tempo liegt beim Mitarbeiter.
 */
export const advanceReflectionSchema = z.object({
  ziel: reflexionsStufeSchema,
  ausloeser: z.enum(["coach", "nutzer"]).default("nutzer"),
  begruendung: z.string().optional(),
})
export type AdvanceReflectionInput = z.infer<typeof advanceReflectionSchema>

export const chatMessageSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.string(),
  createdAt: z.string(),
  fromVoice: z.boolean().optional(),
})

/**
 * Eine Handlung im Verlauf statt einer Äußerung: `*(sie dreht den Bildschirm…)*`
 *
 * Gebraucht, seit das AV-Tool „Ergebnis zeigen" kennt. Das Zeigen IST ein
 * Gesprächsbeitrag — es gehört in den Verlauf, damit die Kundin darauf reagiert
 * und das Feedback es sieht. Es ist aber nichts, was die Beraterin *gesagt*
 * hat, und darf ihr deshalb nicht als Satz zugerechnet werden.
 *
 * Eine Konvention und kein Rollenwert: `MessageRole` ist ein Prisma-Enum mit
 * genau `user | assistant`, und ein dritter Wert zöge Migration, Zod-Schema,
 * Sprechblase, Feedback-Renderer und den Sprach-Persistenzpfad nach sich. Der
 * Preis dieser Lösung ist eine Inhaltsprüfung; wenn die Zeile mehr tragen muss
 * als „anders rendern und anders lesen", ist der Enum-Wert die Reparatur.
 *
 * Alles, was die Konvention kennt, liest von hier — Oberfläche wie Prompts.
 */
const AKTION_AUF = "*("
const AKTION_ZU = ")*"

export function aktionsZeile(text: string): string {
  return `${AKTION_AUF}${text.trim()}${AKTION_ZU}`
}

export function istAktionsZeile(inhalt: string): boolean {
  const roh = inhalt.trim()
  return roh.startsWith(AKTION_AUF) && roh.endsWith(AKTION_ZU)
}

/** Der Text ohne die Klammern — fürs Rendern und fürs Vorlesen. */
export function aktionsInhalt(inhalt: string): string {
  const roh = inhalt.trim()
  return istAktionsZeile(roh)
    ? roh.slice(AKTION_AUF.length, -AKTION_ZU.length).trim()
    : roh
}

export const createSessionSchema = z.object({
  personaId: z.string().min(1),
  coachStyleId: z.string().min(1),
  mode: z.enum(["text", "voice", "mixed"]).default("text"),
  phase: gespraechsPhaseSchema.optional(),
})
export type CreateSessionInput = z.infer<typeof createSessionSchema>

/**
 * Freie Persona-Konfiguration für einen Durchlauf. Überschreibt die
 * gewählte Vorlage-Persona; alle Felder optional (nur Gesetztes greift).
 */
export const processPersonaConfigSchema = z.object({
  name: z.string().optional(),
  age: z.number().int().min(16).max(100).optional(),
  profession: z.string().optional(),
  lifeSituation: z.string().optional(),
  motive: z.string().optional(),
  objections: personaObjectionsSchema.optional(),
  communicationStyle: z.string().optional(),
  difficulty: difficultySchema.optional(),
  // Stammdaten (füllen den Vorbereitungsschritt automatisch vor)
  familienstand: z.string().optional(),
  kinder: z.string().optional(),
  einkommenNetto: z.number().optional(),
  bestandsvorsorge: z.string().optional(),
  wohnsituation: z.string().optional(),
})
export type ProcessPersonaConfig = z.infer<typeof processPersonaConfigSchema>

/** Startet einen Prozess-Durchlauf (konfigurierbare Einheitenliste). */
export const startProcessRunSchema = z.object({
  personaId: z.string().min(1),
  /** The units to train (ordered, at least one). */
  units: trainingsEinheitSchema.array().min(1),
  /**
   * Only for the appointment call: existing customer or cold call. Changes the
   * customer's opening sentences, not the case.
   */
  akquise: akquiseArtSchema.optional(),
  coachStyleId: z.string().min(1).optional(),
  mode: z.enum(["text", "voice", "mixed"]).default("text"),
  /** Freie Konfiguration; überschreibt die Vorlage-Persona. */
  persona: processPersonaConfigSchema.optional(),
})
export type StartProcessRunInput = z.infer<typeof startProcessRunSchema>

/** Schließt die Vorbereitungs-Etappe ab (Vorbereitungsbogen + Gesprächsziel). */
export const submitPreparationSchema = z.object({
  checklist: z.array(
    z.object({
      key: z.string().min(1),
      checked: z.boolean(),
      /** Anzeige-Label des Eintrags (für freie Telefonat-Fakten). */
      label: z.string().max(120).optional(),
      /** Vom Berater bestätigter/bearbeiteter Wert des Eintrags. */
      value: z.string().max(400).optional(),
    })
  ),
  goal: z.string().max(2000).default(""),
})
export type SubmitPreparationInput = z.infer<typeof submitPreparationSchema>

/**
 * Completes the follow-up work (the sheet along the leading questions).
 *
 * Nothing is required: as with the preparation sheet, whatever stands there is
 * what gets submitted. An empty answer reads as `(offen)` in the prompt, where
 * it is the coach's cue for the next question.
 */
export const submitNachbereitungSchema = z.object({
  antworten: z.array(
    z.object({ key: z.string().min(1), value: z.string().max(2000) })
  ),
})
export type SubmitNachbereitungInput = z.infer<typeof submitNachbereitungSchema>

/**
 * Speichert den Stand des AV-Tools im Fallzustand.
 *
 * Nur die Eingabe wandert über die Leitung, nie das Ergebnis: der Server
 * rechnet aus ihr neu. Dieselbe Haltung wie beim Bedarfsanalyse-Sidecar — das
 * Modell darf vorschlagen, der Server entscheidet; hier darf der Browser
 * tippen, der Server rechnet.
 *
 * `zeigen` ist der Knopf „Ergebnis zeigen" und die einzige Stelle, an der eine
 * getippte Zahl den KI-Kunden erreicht. Ohne ihn bleibt das Werkzeug ein
 * Blatt, in das nur die Beraterin sieht.
 */
export const saveAvToolSchema = z.object({
  eingabe: avToolEingabeBasisSchema,
  zeigen: z.boolean().default(false),
})
export type SaveAvToolInput = z.infer<typeof saveAvToolSchema>

/**
 * Stores the product selection of "Lösung & Abschluss". `zeigen` turns the
 * screen towards the customer: only then does the selection reach her.
 */
export const saveLoesungSchema = z.object({
  vorschlaege: z.array(loesungVorschlagSchema).max(LOESUNG_PRODUKTE.length),
  zeigen: z.boolean().default(false),
})
export type SaveLoesungInput = z.infer<typeof saveLoesungSchema>

export const appendMessagesSchema = z.object({
  messages: z.array(chatMessageSchema).min(1),
})

/**
 * Bericht des Clients nach einem Sprachgespräch.
 *
 * Notwendig, weil der Realtime-Pfad über WebRTC direkt vom Browser zum
 * Modellanbieter läuft. Der Server sieht die Turns sonst nie.
 */
export const reportVoiceTurnsSchema = z.object({
  turns: z.array(chatMessageSchema),
  audioSeconds: z.number().min(0),
  model: z.string().min(1),
})

export const heartbeatSchema = z.object({
  activeSeconds: z.number().int().min(0),
  idleSeconds: z.number().int().min(0),
})

export const completeSessionSchema = z.object({
  activeSeconds: z.number().int().min(0),
  idleSeconds: z.number().int().min(0),
})

/**
 * Anforderung des dialogischen Feedbacks.
 *
 * `refresh` erzeugt eine neue Fassung, obwohl schon eine steht. Ohne das Flag
 * liefert der Server das gespeicherte Feedback aus — das ist der Weg zum
 * Öffnen, nicht zum Nachziehen, und er kostet keinen Modellaufruf.
 */
export const feedbackStreamRequestSchema = z.object({
  refresh: z.boolean().optional(),
})
export type FeedbackStreamRequest = z.infer<typeof feedbackStreamRequestSchema>

export const rateFeedbackSchema = z.object({
  score: z.number().int().min(1).max(5),
  comment: z.string().max(2000).optional(),
})
export type RateFeedbackInput = z.infer<typeof rateFeedbackSchema>

/**
 * Restricted to relative paths under `/personas/`. A free URL field would let a
 * tenant admin make the app load arbitrary third-party content (referrer leak,
 * tracking pixel) for every advisor who opens the case picker.
 */
export const personaPortraitUrlSchema = z
  .string()
  .regex(
    /^\/personas\/[a-z0-9][a-z0-9-]*\.(webp|png|jpg)$/,
    "Portrait muss ein Pfad unter /personas/ sein."
  )
  .optional()

export const upsertPersonaSchema = z.object({
  id: z.string().min(1).optional(),
  name: z.string().min(1),
  age: z.number().int().min(16).max(100),
  profession: z.string().min(1),
  lifeSituation: z.string().min(1),
  motive: z.string().min(1),
  objections: personaObjectionsSchema,
  fall: personaFallSchema,
  profil: personaProfilSchema.default({}),
  communicationStyle: z.string().min(1),
  difficulty: difficultySchema,
  status: z.enum(["draft", "active", "archived"]),
  voice: realtimeVoiceSchema.optional(),
  portraitUrl: personaPortraitUrlSchema,
})
export type UpsertPersonaInput = z.infer<typeof upsertPersonaSchema>

/** Same restriction as persona portraits: relative paths only. */
export const coachPortraitUrlSchema = z
  .string()
  .regex(
    /^\/coaches\/[a-z0-9][a-z0-9-]*\.(webp|png|jpg)$/,
    "Portrait muss ein Pfad unter /coaches/ sein."
  )
  .optional()

/**
 * Same restriction again, for the spoken sample on the coach card. Audio from a
 * free URL would be a third-party request on the onboarding page.
 */
export const coachVoiceSampleUrlSchema = z
  .string()
  .regex(
    /^\/coaches\/[a-z0-9][a-z0-9-]*\.(mp3|m4a|wav)$/,
    "Stimmprobe muss ein Pfad unter /coaches/ sein."
  )
  .optional()

/**
 * Onboarding and profile edits share one endpoint: both set the display name
 * and the chosen coach, the only difference is when they happen.
 */
export const updateProfileSchema = z.object({
  name: z.string().trim().min(2, "Bitte gib deinen Namen an.").max(80),
  coachStyleId: z.string().min(1, "Bitte wähle einen Coach."),
})
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>

/**
 * The chunks of the product tour ("Rundgang"), one per area of the app. Each
 * starts on its own the first time the advisor reaches that area and is
 * recorded separately, so seeing the home page does not silence the chat one.
 */
export const tourChunkSchema = z.enum(["start", "training", "chat", "pruefung"])
export type TourChunkId = z.infer<typeof tourChunkSchema>

export const completeTourSchema = z.object({ tour: tourChunkSchema })
export type CompleteTourInput = z.infer<typeof completeTourSchema>

/**
 * Raw answers from the onboarding chat, merged incrementally as the advisor
 * answers. All fields optional: each tool call is a patch, not a snapshot.
 * Direct quotes only — the interpretation happens in the derived essence.
 */
export const essenceAnswersSchema = z.object({
  experienceYears: z.number().int().min(0).max(60).optional(),
  focusAreas: z.array(z.string().max(200)).max(8).optional(),
  selfStatedStrengths: z.array(z.string().max(200)).max(8).optional(),
  goals: z.array(z.string().max(200)).max(8).optional(),
})
export type EssenceAnswers = z.infer<typeof essenceAnswersSchema>

export const essenceEntrySchema = z.object({
  title: z.string().min(1).max(80),
  description: z.string().min(1).max(300),
})
export type EssenceEntry = z.infer<typeof essenceEntrySchema>

/**
 * The derived learning essence: what the home page shows. Two entries per
 * category, no more — a summary with ten priorities is none, and the three
 * columns on the home page only stay comparable while they stay short.
 */
export const learningEssenceContentSchema = z.object({
  priorities: z.array(essenceEntrySchema).min(1).max(2),
  strengths: z.array(essenceEntrySchema).min(1).max(2),
  actionFields: z.array(essenceEntrySchema).min(1).max(2),
})
export type LearningEssenceContent = z.infer<
  typeof learningEssenceContentSchema
>

export const upsertCoachStyleSchema = z.object({
  id: z.string().min(1).optional(),
  name: z.string().min(1),
  headline: z.string().default(""),
  age: z.number().int().min(18).max(99).default(40),
  portraitUrl: coachPortraitUrlSchema,
  voice: realtimeVoiceSchema.optional(),
  voiceSampleUrl: coachVoiceSampleUrlSchema,
  strengths: z.array(z.string()).default([]),
  description: z.string().min(1),
  tone: z.string().min(1),
  directness: z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
  ]),
  length: z.enum(["short", "medium", "detailed"]),
  status: z.enum(["active", "inactive"]),
  examplePhrase: z.string().min(1),
})
export type UpsertCoachStyleInput = z.infer<typeof upsertCoachStyleSchema>

// Low on purpose: participants pick their password during the workshop right
// after the one-time login and should be able to type it quickly.
export const MIN_PASSWORD_LENGTH = 6

export const upsertUserSchema = z.object({
  id: z.string().min(1).optional(),
  name: z.string().min(1),
  email: z.string().email(),
  /** `platform` makes the account an admin; only admins may assign it. */
  role: z.enum(["advisor", "coach", "manager", "platform"]),
  /** Omitted keeps the current group, `null` takes the account out of it. */
  teamId: z.string().nullable().optional(),
  status: z.enum(["invited", "active", "disabled"]),
  /**
   * Nur für die Plattformrolle: der Mandant, in dem der Nutzer angelegt wird.
   * Damit lassen sich Konten aus der Sparkassen-Detailansicht heraus pflegen,
   * ohne vorher in den Mandanten zu wechseln. Für alle anderen Rollen wird das
   * Feld ignoriert; dort gilt weiterhin der eigene Mandant.
   */
  tenantId: z.string().min(1).optional(),
})
export type UpsertUserInput = z.infer<typeof upsertUserSchema>

export const oneTimePasswordVariantSchema = z.enum(["random", "email"])
export type OneTimePasswordVariant = z.infer<typeof oneTimePasswordVariantSchema>

// A missing body means "random": callers from before the variant existed send
// none, and a validation error there would lock managers out of the fallback.
export const issueOneTimePasswordSchema = z.preprocess(
  (body) => body ?? {},
  z.object({ variant: oneTimePasswordVariantSchema.default("random") }),
)
export type IssueOneTimePasswordInput = z.infer<typeof issueOneTimePasswordSchema>

export const roleplayRequestSchema = z.object({
  sessionId: z.string().min(1),
  messages: z.array(
    z.object({
      role: z.enum(["user", "assistant", "system"]),
      content: z.string(),
    })
  ),
})

/**
 * The text coach chat in the review step. Same wire shape as the roleplay
 * request: only the sessionId plus the running conversation. The server loads
 * the transcript, persona and feedback itself and checks ownership — the
 * client never sends privileged context.
 */
export const coachChatRequestSchema = z.object({
  sessionId: z.string().min(1),
  messages: z.array(
    z.object({
      role: z.enum(["user", "assistant", "system"]),
      content: z.string(),
    })
  ),
  /**
   * Gewünschte Reflexionsstufe.
   *
   * Only ever moves the conversation forward: the server derives the stage from
   * the stored session and takes this as a floor, never a ceiling. A stale
   * client cannot pull the coach back into asking questions after the analysis
   * has begun.
   */
  stufe: reflexionsStufeSchema.optional(),
})

/**
 * The run-wide coach chat below the debrief. Same principle as the review
 * coach, but scoped to a whole Durchlauf: the runId travels in the URL path,
 * only the running conversation is in the body. The server loads every phase's
 * transcript and feedback itself and checks run ownership.
 */
export const runCoachChatRequestSchema = z.object({
  messages: z.array(
    z.object({
      role: z.enum(["user", "assistant", "system"]),
      content: z.string(),
    })
  ),
})

/**
 * The open coach chat. Unlike the review coach it is not bound to a session and
 * needs no feedback: only the running conversation and the mode travel.
 */
export const coachGeneralChatRequestSchema = z.object({
  messages: z.array(
    z.object({
      role: z.enum(["user", "assistant", "system"]),
      content: z.string(),
    })
  ),
  /** Which conversation this turn belongs to. A new one is opened when absent. */
  threadId: z.string().min(1).optional(),
  /** In training mode the coach asks the questions instead of answering them. */
  mode: chatModeSchema.default("open"),
  /** Training mode only: which phase the questions are drawn from. */
  trainingPhase: gespraechsPhaseSchema.optional(),
  /** IDD mode only: the IDD product training this conversation belongs to. */
  iddTrainingId: z.string().min(1).optional(),
  /**
   * Training mode only: which product is being quizzed. Without it the coach
   * keeps refusing product statements, so an unknown key is harmless.
   */
  produkt: z.string().min(1).optional(),
  /**
   * Prüfung mode only: which subject area the questions are drawn from.
   *
   * Deliberately nothing about the graded answer: the server reads the open
   * evaluation from the thread itself. An id carried by the client was lost on
   * every dropped or retried turn, and the coach then asked for an answer that
   * was already on the card.
   */
  lernfeld: z.string().min(1).optional(),
  /**
   * Prüfung mode only: how many questions this run targets. Sent with the
   * opening turn and stored on the thread; later turns leave it out, and a
   * missing value never clears it. Upper bound mirrors PRUEFUNG_ZIEL_MAX,
   * which cannot be imported here without a cycle.
   */
  ziel: z.number().int().min(1).max(200).optional(),
})

/** Wie reportVoiceTurns, aber für einen Coach-Thread statt einer Sitzung. */
export const reportThreadVoiceTurnsSchema = z.object({
  turns: z.array(chatMessageSchema),
  audioSeconds: z.number().min(0),
  model: z.string().min(1),
  mode: chatModeSchema.optional(),
})
export type ReportThreadVoiceTurnsInput = z.infer<
  typeof reportThreadVoiceTurnsSchema
>

/**
 * Upper bound for one synthesis. Raised from 4000 for NL-13: the whole feedback
 * document goes to the voice in a single call, because every request starts a
 * new take and splitting it would leave audible seams between the blocks.
 *
 * Exported so the web-side text builder can clamp against the same number — it
 * would otherwise only find out at the 400.
 */
export const TTS_MAX_CHARS = 12000

export const ttsSchema = z.object({
  text: z.string().min(1).max(TTS_MAX_CHARS),
  // Dasselbe Stimmenvokabular wie im Live-Gespräch: das Vorlesen im Textchat
  // nutzt die Stimme der Persona, damit dieselbe Kundin im Text und am Telefon
  // gleich klingt. Als Enum statt als freier String, weil eine unbekannte
  // Stimme sonst erst beim Anbieter auffällt und dort als 503 landet.
  voice: realtimeVoiceSchema.optional(),
  sessionId: z.string().optional(),
})

/**
 * Modes a thread can be opened in directly, with its first message already
 * written. Both are entered from their own page and must never be reopened as
 * an ordinary chat — an old `/chat` link would otherwise silently turn an
 * appointment preparation into a quiz.
 */
export const startContextModeSchema = z.enum(["preparation", "nachbereitung"])
export type StartContextMode = z.infer<typeof startContextModeSchema>

export const createCoachThreadSchema = z
  .object({
    /** The first message the thread is created with. */
    startContext: z.string().trim().min(1).max(2000).optional(),
    /** Only together with `startContext`; without it the thread stays in the default mode. */
    startMode: startContextModeSchema.default("preparation"),
  })
  .default({ startMode: "preparation" })
export type CreateCoachThreadInput = z.infer<typeof createCoachThreadSchema>

export const updateCoachThreadSchema = z
  .object({
    title: z.string().trim().min(1).max(120).optional(),
    favorite: z.boolean().optional(),
  })
  .refine((data) => data.title !== undefined || data.favorite !== undefined, {
    message: "Mindestens ein Feld angeben.",
  })
export type UpdateCoachThreadInput = z.infer<typeof updateCoachThreadSchema>
